/* ============================================================================
   cl-hero-slider.js — horizontal banner slideshow for the homepage hero
   ----------------------------------------------------------------------------
   Loaded (deferred) from sections/cl-home-hero.liquid, and ONLY when that
   section has two or more `banner` blocks. A single-banner hero never fetches
   this file: it has no carousel chrome, no transform and nothing to drive.

   PROGRESSIVE ENHANCEMENT IS THE WHOLE DESIGN. The track is a flex row of
   100%-wide slides inside an overflow:hidden hero, so with no JS at all the
   first banner renders exactly as a static hero would — correct copy, correct
   artwork, correct crop. Nothing here is required for the hero to be usable,
   which is why it is safe to defer and safe for it to fail. This mirrors the
   guard pattern in cl-motion.js: the storefront must never depend on a script
   to show content.

   Contract with cl-home.css:
     --cl-hero-i ......... 0-based active index; the track translates by
                           calc(var(--cl-hero-i) * -100%)
     --cl-hero-drag ...... live finger offset in px while swiping
     .is-active .......... on the current .cl-hero__slide and .cl-hero__dot

   Contract with cl-hero-slide.liquid:
     data-cl-bg / data-cl-bg-m  background URLs for slides 2..n, applied here
                                after window load — see hydrate() for why.
   ============================================================================ */
(function () {
  'use strict';

  /* A second copy of this tag can appear when the theme editor swaps the
     section's DOM. Re-registering is enough; re-installing the document-level
     listeners would double every one of them. */
  if (window.clHeroSlider) {
    window.clHeroSlider.register();
    return;
  }

  var ROOT_SEL = '[data-cl-hero]';
  var INIT_ATTR = 'data-cl-hero-init';

  /* Distance a finger must travel before a swipe counts. 40px is far enough
     that a tap on the CTA never registers as one, close enough that a flick
     does. Measured against the raw offset, not the damped one. */
  var SWIPE_MIN = 40;
  /* Movement below this is not yet an intent — used to decide horizontal vs
     vertical ONCE per gesture, so a diagonal scroll cannot flip mid-drag. */
  var INTENT_MIN = 8;
  /* Elements this file is allowed to take out of the tab order. Deliberately
     NOT a generic focusable query: stripping tabindex off something an author
     set it on would be a silent a11y regression. */
  var OWNED_FOCUSABLE = 'a.cl-hero__link, a.cl-hero__cta';

  function prefersReduced() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  function init(root) {
    if (root.getAttribute(INIT_ATTR) === '1') return;

    var track = root.querySelector('.cl-hero__track');
    var slides = [].slice.call(root.querySelectorAll('[data-cl-hero-slide]'));
    /* One slide is a static hero. Bail BEFORE marking it initialised so that if
       the editor later adds a banner, the re-render initialises properly. */
    if (!track || slides.length < 2) return;
    root.setAttribute(INIT_ATTR, '1');

    var dots = [].slice.call(root.querySelectorAll('[data-cl-hero-dot]'));

    var interval = parseInt(root.getAttribute('data-cl-interval'), 10);
    if (!isFinite(interval) || interval < 1500) interval = 6000;
    /* Reduced motion opts OUT of the rotation entirely rather than merely
       shortening it — an auto-advancing banner is exactly the kind of
       unrequested movement the setting exists to stop. The arrows and dots
       still work, so no content becomes unreachable. */
    var autoplay = root.getAttribute('data-cl-autoplay') === '1' && !prefersReduced();
    var pauseOnHover = root.getAttribute('data-cl-pause-hover') !== '0';

    var index = 0;
    var timer = null;
    var hovering = false;
    var focused = false;

    /* ── Lazy artwork ──────────────────────────────────────────────────────
       A CSS background on an in-flow element is fetched immediately even when
       the element sits off-screen, so emitting every banner's URL server-side
       would put N-1 full-width images in direct competition with this
       section's own background — which is the measured homepage LCP on both
       breakpoints. Slides 2..n therefore carry their URLs as data attributes
       and get them after window load. With JS off there is no way to reach
       those slides, so nothing is lost by them being unpainted. */
    function hydrate(el) {
      if (!el || el.getAttribute('data-cl-hydrated') === '1') return;
      var bg = el.getAttribute('data-cl-bg');
      var bgm = el.getAttribute('data-cl-bg-m');
      if (bg) el.style.setProperty('--cl-hero-img', 'url(' + bg + ')');
      if (bgm) el.style.setProperty('--cl-hero-img-m', 'url(' + bgm + ')');
      el.setAttribute('data-cl-hydrated', '1');
    }

    function hydrateAll() {
      for (var n = 0; n < slides.length; n++) hydrate(slides[n]);
    }

    function paint() {
      track.style.setProperty('--cl-hero-i', index);
      for (var n = 0; n < slides.length; n++) {
        var on = n === index;
        var slide = slides[n];
        if (on) slide.classList.add('is-active');
        else slide.classList.remove('is-active');
        /* An off-screen banner is still in the DOM, so without this its link is
           announced by a screen reader and reachable by Tab while invisible. */
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        var owned = slide.querySelectorAll(OWNED_FOCUSABLE);
        for (var k = 0; k < owned.length; k++) {
          if (on) owned[k].removeAttribute('tabindex');
          else owned[k].setAttribute('tabindex', '-1');
        }
      }
      for (var d = 0; d < dots.length; d++) {
        if (d === index) {
          dots[d].classList.add('is-active');
          dots[d].setAttribute('aria-current', 'true');
        } else {
          dots[d].classList.remove('is-active');
          dots[d].removeAttribute('aria-current');
        }
      }
    }

    function go(n) {
      var len = slides.length;
      index = ((n % len) + len) % len; // wraps in both directions
      hydrate(slides[index]);
      paint();
    }

    /* ── Autoplay ──────────────────────────────────────────────────────────
       Stopped for good on any deliberate navigation (arrow, dot, swipe,
       arrow key). That is the "mechanism to pause" WCAG 2.2.2 asks for, and it
       matches what people expect: once you have taken hold of a carousel it
       should not yank itself out from under you a few seconds later. */
    function tick() {
      if (hovering || focused || document.hidden) return;
      go(index + 1);
    }

    function start() {
      if (!autoplay || timer) return;
      timer = setInterval(tick, interval);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function surrender() {
      autoplay = false;
      stop();
    }

    /* ── Controls ──────────────────────────────────────────────────────────── */
    var prev = root.querySelector('[data-cl-hero-prev]');
    var next = root.querySelector('[data-cl-hero-next]');
    if (prev) prev.addEventListener('click', function () { surrender(); go(index - 1); });
    if (next) next.addEventListener('click', function () { surrender(); go(index + 1); });
    for (var d = 0; d < dots.length; d++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          surrender();
          go(parseInt(btn.getAttribute('data-cl-hero-dot'), 10) || 0);
        });
      })(dots[d]);
    }

    root.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      surrender();
      go(e.key === 'ArrowLeft' ? index - 1 : index + 1);
    });

    if (pauseOnHover) {
      root.addEventListener('mouseenter', function () { hovering = true; });
      root.addEventListener('mouseleave', function () { hovering = false; });
    }
    /* Focus pause is unconditional: rotating the banner out from under someone
       tabbing through its links loses their place mid-task. */
    root.addEventListener('focusin', function () { focused = true; });
    root.addEventListener('focusout', function () { focused = false; });

    /* ── Swipe ─────────────────────────────────────────────────────────────
       The track follows the finger (transition suspended for the duration) and
       commits on release. .cl-hero__track has touch-action:pan-y in the CSS,
       so vertical scrolling is still owned by the browser and never fights
       this. Horizontal vs vertical intent is decided once, on the first 8px. */
    var pointerId = null;
    var startX = 0;
    var startY = 0;
    var rawDx = 0;
    var decided = false;
    var horizontal = false;
    /* Timestamp, not a boolean: a bare "swallow the next click" flag would still
       be set minutes later and would eat the first Enter press of someone
       tabbing to a dot (a keyboard click fires with no pointerdown to clear it).
       Bounding it to the gesture keeps the suppression to what it is for. */
    var swipedAt = 0;

    function endDrag() {
      if (pointerId === null) return;
      pointerId = null;
      track.style.transition = '';
      track.style.setProperty('--cl-hero-drag', '0px');
      if (horizontal && Math.abs(rawDx) > SWIPE_MIN) {
        swipedAt = Date.now(); // suppress the click this gesture would otherwise fire
        surrender();
        go(rawDx < 0 ? index + 1 : index - 1);
      }
      rawDx = 0;
      decided = false;
      horizontal = false;
    }

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      rawDx = 0;
      decided = false;
      horizontal = false;
      swipedAt = 0;
      hydrateAll(); // the neighbour must already be painted when it slides in
    });

    track.addEventListener('pointermove', function (e) {
      if (pointerId === null || e.pointerId !== pointerId) return;
      var mx = e.clientX - startX;
      var my = e.clientY - startY;
      if (!decided) {
        if (Math.abs(mx) < INTENT_MIN && Math.abs(my) < INTENT_MIN) return;
        decided = true;
        horizontal = Math.abs(mx) > Math.abs(my);
        if (horizontal) {
          track.style.transition = 'none';
          stop();
        }
      }
      if (!horizontal) return;
      rawDx = mx;
      /* The track is a plain strip, not an infinite loop, so there is no
         neighbour beyond either end to reveal. Damp the pull instead of
         exposing blank space; the release still wraps. */
      var atStart = index === 0 && mx > 0;
      var atEnd = index === slides.length - 1 && mx < 0;
      track.style.setProperty('--cl-hero-drag', (atStart || atEnd ? mx * 0.25 : mx) + 'px');
    });

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);

    /* A swipe that starts on the whole-banner link would otherwise navigate on
       release. Capture phase, so it runs before the anchor's own default. */
    root.addEventListener(
      'click',
      function (e) {
        if (!swipedAt || Date.now() - swipedAt > 400) return;
        swipedAt = 0;
        e.preventDefault();
        e.stopPropagation();
      },
      true,
    );

    /* ── Theme editor ──────────────────────────────────────────────────────
       Selecting a banner in the sidebar must show that banner, or the merchant
       edits banner 3 while looking at banner 1. Needs {{ block.shopify_attributes }}
       on the slide, which cl-hero-slide.liquid emits. */
    root.addEventListener('shopify:block:select', function (e) {
      var slide = e.target.closest ? e.target.closest('[data-cl-hero-slide]') : null;
      if (!slide) return;
      stop();
      go(parseInt(slide.getAttribute('data-cl-hero-slide'), 10) || 0);
    });
    root.addEventListener('shopify:block:deselect', start);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    paint();
    if (document.readyState === 'complete') hydrateAll();
    else window.addEventListener('load', hydrateAll);
    start();
  }

  function register(scope) {
    var root = scope && scope.nodeType === 1 ? scope : document;
    try {
      if (root.matches && root.matches(ROOT_SEL)) init(root);
      var found = root.querySelectorAll(ROOT_SEL);
      for (var n = 0; n < found.length; n++) init(found[n]);
    } catch (e) {
      /* A DOM surprise must never leave the hero broken — the first banner is
         already painted and correct without any of this. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { register(); });
  } else {
    register();
  }

  document.addEventListener('shopify:section:load', function (e) { register(e.target); });

  window.clHeroSlider = { register: register };
})();
